import { useState, useRef, useEffect } from 'react';
import { Bot, X, Send, Minimize2, Maximize2, Loader2, Zap } from 'lucide-react';
import { sendChatMessage, ChatMessage } from '../services/openaiService';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

export function AIAssistantChat() {
  const { profile } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      role: 'assistant',
      content: 'Hello! I\'m Apollo Intelligence, your smart fleet assistant with full database access. I can help you with:\n\n💰 **Revenue & Earnings**\n• Weekly/monthly revenue reports\n• Driver earnings and pay\n• Revenue per mile analysis\n\n🚛 **Fleet Analytics**\n• Truck mileage tracking\n• Driver performance stats\n• Load completion metrics\n\n🔧 **Maintenance**\n• Maintenance costs per truck\n• Parts vs labor breakdown\n• Service history\n\n📍 **Operations**\n• Driver locations and assignments\n• Current load status\n• Fleet overview\n\nTry the quick actions below or ask me anything!',
      timestamp: new Date(),
    },
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  const isAllowed = profile?.role === 'Superadmin' || profile?.role === 'Admin';

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (!isAllowed) return;
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isAllowed]);

  // Focus input when chat opens
  useEffect(() => {
    if (!isAllowed) return;
    if (isOpen && !isMinimized) {
      inputRef.current?.focus();
    }
  }, [isOpen, isMinimized, isAllowed]);

  const handleSendMessage = async (customMessage?: string) => {
    const messageToSend = customMessage || inputMessage.trim();
    if (!messageToSend || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: messageToSend,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    if (!customMessage) {
      setInputMessage('');
    }
    setIsLoading(true);

    try {
      // Convert messages to ChatMessage format for API
      const conversationHistory: ChatMessage[] = messages.map(msg => ({
        role: msg.role,
        content: msg.content,
      }));

      const result = await sendChatMessage(messageToSend, conversationHistory);

      if (result.success && result.message) {
        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: result.message,
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, assistantMessage]);
      } else {
        toast.error(result.error || 'Failed to get response from AI');
        const errorMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: `Sorry, I encountered an error: ${result.error || 'Unknown error'}`,
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, errorMessage]);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const clearChat = () => {
    setMessages([
      {
        id: '1',
        role: 'assistant',
        content: 'Chat cleared. How can I help you?',
        timestamp: new Date(),
      },
    ]);
  };

  // Simple markdown renderer for bold text
  const renderMarkdown = (text: string) => {
    // Split by ** for bold
    const parts = text.split(/(\*\*.*?\*\*)/g);
    
    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        // Remove ** and render as bold
        const boldText = part.slice(2, -2);
        return <strong key={index} className="font-bold">{boldText}</strong>;
      }
      return <span key={index}>{part}</span>;
    });
  };

  const quickActions = [
    { 
      label: '💰 Weekly Revenue', 
      query: 'What is our revenue for this week?',
      color: 'from-green-500/20 to-emerald-500/20 border-green-500/30 hover:border-green-500/60'
    },
    { 
      label: '🏆 Top Drivers', 
      query: 'Show me top 5 drivers by revenue this week',
      color: 'from-blue-500/20 to-indigo-500/20 border-blue-500/30 hover:border-blue-500/60'
    },
    { 
      label: '👥 Top Customers', 
      query: 'Who are our top customers by revenue?',
      color: 'from-purple-500/20 to-pink-500/20 border-purple-500/30 hover:border-purple-500/60'
    },
    { 
      label: '🚛 Idle Trucks', 
      query: 'Which trucks are sitting idle?',
      color: 'from-orange-500/20 to-red-500/20 border-orange-500/30 hover:border-orange-500/60'
    },
    { 
      label: '🔧 Maintenance', 
      query: 'Show me maintenance costs this month',
      color: 'from-yellow-500/20 to-amber-500/20 border-yellow-500/30 hover:border-yellow-500/60'
    },
    { 
      label: '📋 Dispatchers', 
      query: 'Show me dispatcher performance this week',
      color: 'from-cyan-500/20 to-teal-500/20 border-cyan-500/30 hover:border-cyan-500/60'
    },
  ];

  // Only show AI chat for Superadmin and Admin
  if (!isAllowed) {
    return null;
  }

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 bg-gradient-to-r from-purple-600 via-purple-500 to-indigo-600 text-white p-4 rounded-full shadow-2xl hover:shadow-purple-500/50 transition-all hover:scale-110 z-[1100] group backdrop-blur-xl"
        aria-label="Open AI Assistant"
        style={{
          boxShadow: '0 10px 40px rgba(147, 51, 234, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1) inset'
        }}
      >
        <div className="relative">
          <div className="absolute inset-0 bg-white/30 rounded-full blur-md animate-pulse"></div>
          <Bot className="w-6 h-6 relative" />
        </div>
      </button>
    );
  }

  return (
    <div
      className={`fixed right-6 z-[1100] transition-all duration-300 ${
        isMinimized ? 'bottom-52' : 'bottom-52'
      }`}
      style={{ 
        width: isMinimized ? '320px' : '420px',
        maxHeight: isMinimized ? '60px' : '500px'
      }}
    >
      <div 
        className="bg-gradient-to-b from-slate-900/95 to-slate-950/95 backdrop-blur-2xl rounded-3xl shadow-2xl border border-white/10 overflow-hidden"
        style={{
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.05) inset, 0 0 60px rgba(147, 51, 234, 0.2)'
        }}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600/20 via-purple-500/20 to-indigo-600/20 backdrop-blur-xl border-b border-white/10 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="absolute inset-0 bg-purple-500 rounded-full blur-lg opacity-50 animate-pulse"></div>
              <div className="relative bg-gradient-to-br from-purple-500 to-indigo-600 p-2.5 rounded-full">
                <Bot className="w-5 h-5 text-white" />
              </div>
            </div>
            <div>
              <h3 className="font-bold text-sm text-white">Apollo Intelligence</h3>
              <p className="text-xs text-purple-300">Your Smart Fleet Assistant</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsMinimized(!isMinimized)}
              className="p-2 hover:bg-white/10 rounded-full transition-colors text-white/70 hover:text-white"
              aria-label={isMinimized ? 'Maximize' : 'Minimize'}
            >
              {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
            </button>
            <button
              onClick={() => setIsOpen(false)}
              className="p-2 hover:bg-white/10 rounded-full transition-colors text-white/70 hover:text-white"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

      {/* Chat Content */}
      {!isMinimized && (
        <>
          {/* Quick Actions */}
          <div className="p-4 bg-gradient-to-b from-purple-900/20 to-transparent border-b border-white/5">
              <div className="flex items-center gap-2 mb-3">
                <Zap className="w-4 h-4 text-purple-400" />
                <span className="text-xs font-semibold text-purple-300">Quick Actions</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {quickActions.map((action, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSendMessage(action.query)}
                    disabled={isLoading}
                    className={`text-left px-3 py-2.5 bg-gradient-to-br ${action.color} border rounded-xl transition-all text-xs font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed backdrop-blur-sm hover:scale-105 active:scale-95 shadow-lg`}
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            </div>

          {/* Messages */}
          <div className="overflow-y-auto p-4 space-y-4" style={{ height: '320px', minHeight: '320px', maxHeight: '320px' }}>
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl p-4 backdrop-blur-xl ${
                    message.role === 'user'
                      ? 'bg-gradient-to-br from-purple-600 to-indigo-600 text-white shadow-lg shadow-purple-500/20'
                      : 'bg-white/10 border border-white/20 text-white shadow-lg'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap leading-relaxed">
                    {renderMarkdown(message.content)}
                  </p>
                  <p
                    className={`text-xs mt-2 ${
                      message.role === 'user' ? 'text-purple-200' : 'text-white/50'
                    }`}
                  >
                    {message.timestamp.toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </p>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white/10 border border-white/20 rounded-2xl p-4 backdrop-blur-xl">
                  <div className="flex items-center gap-2 text-purple-300">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">Thinking...</span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="p-5 pb-6 border-t border-white/10 bg-gradient-to-t from-slate-950/50 to-transparent backdrop-blur-xl">
            <div className="flex items-center gap-3 mb-3">
              <input
                ref={inputRef}
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask me anything about your fleet..."
                className="flex-1 px-4 py-3.5 bg-white/10 border border-white/20 rounded-2xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500/50 text-sm text-white placeholder-white/40 backdrop-blur-xl transition-all focus:bg-white/15"
                disabled={isLoading}
                style={{
                  outline: 'none'
                }}
              />
              <button
                onClick={() => handleSendMessage()}
                disabled={!inputMessage.trim() || isLoading}
                className="p-3.5 bg-gradient-to-br from-purple-600 to-indigo-600 text-white rounded-2xl hover:from-purple-500 hover:to-indigo-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-purple-500/30 hover:shadow-purple-500/50"
                aria-label="Send message"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
            <div className="flex items-center justify-between px-1">
              <button
                onClick={clearChat}
                className="text-xs text-white/50 hover:text-white/80 transition-colors"
              >
                Clear chat
              </button>
              <p className="text-xs text-white/40">
                Press Enter to send
              </p>
            </div>
          </div>
        </>
      )}
      </div>
    </div>
  );
}
